import { Field, FieldMetaProps, FieldProps, FieldValidator } from "formik";

// Components
import { CheckboxField, ICheckboxFieldProps } from "./CheckboxField";
import { ColorPickField, IColorPickFieldProps } from "./ColorPickField";
import { DateTimeField, IDateTimeFieldProps } from "./DateTimeField";
import { DraggableSelectField } from "./DraggableSelectField";
import { IMaskTextFieldProps, MaskTextField } from "./MaskTextField";
import { IPhoneFieldProps, PhoneField } from "./PhoneNumberField";
import { IRichTextFieldProps, RichTextField } from "./RichTextField";
import { ISelectFieldProps, SelectField } from "./SelectField";
import { ISwitchFieldProps, SwitchField } from "./SwitchField";
import { ITextFieldProps, TextField } from "./TextField";
import { ITimeFieldProps, TimeField } from "./TimeField";
import { IToggleGroupProps, ToggleGroup } from "./ToggleGroup";

interface IExtendedFieldProps {
  validate?: FieldValidator; // Validate a field and return an error string.
  format?: (value: any) => any; // Format a supplied value before it's passed to component.
  parse?: (value: any) => any; // Parse an output value before it's sent back to field.
  onChange?: (value: any, event?: any) => void;
  defaultValue?: any;
  meta?: Partial<FieldMetaProps<any>>;
  form?: any;
  field?: any;
}

interface IModifiedProps {
  name: string;
  value: any;
  onChange: (event: any) => any;
  onBlur: (event: any) => any;
  meta: FieldMetaProps<any>;
  form?: any;
  field?: any;
}

/**
 * Field parameter function that returns formik field props to the underlying field component.
 * Main purpose of this function is to format (incoming) and parse (outgoing) values if those functions are supplied.
 * @param fieldProps - field props supplied by formik field.
 * @param props - props supplied by wrapper component.
 */
const fieldParameters = (fieldProps: FieldProps, props: IExtendedFieldProps): IModifiedProps => {
  const { format, parse, defaultValue } = props;
  const { field, form } = fieldProps;

  const { setFieldValue, setFieldTouched } = form;
  const { name, value, onBlur, onChange } = field;

  // If formik's field meta if available or props passed in by user if not.
  const meta = { ...fieldProps.meta, ...props.meta };

  // Format the input value before passing to field component if function is supplied.
  const fieldValue = format && typeof format === "function" && value !== undefined ? format(value) : value;
  const defaultFieldValue =
    format && typeof format === "function" && defaultValue !== undefined ? format(defaultValue) : defaultValue;

  // If value is undefined but there is a default value assigned, pass that as the value as all fields are controlled.
  const showValue = fieldValue ?? defaultFieldValue;

  const fieldOnChange = (event: any, checkBoxValue?: boolean): any => {
    const actualValue = event?.target ? event.target.value : event;
    if (actualValue === "undefined") {
      return event;
    }

    const outputValue: any =
      checkBoxValue === true || checkBoxValue === false
        ? parse && typeof parse === "function"
          ? parse(checkBoxValue)
          : checkBoxValue
        : parse && typeof parse === "function"
          ? parse(actualValue)
          : actualValue;

    // Parse the output value before passing to field if function is supplied.
    if (event && event.type && event.target) {
      event.target.value = outputValue; // this ensures that if parsed, the new event value is updated.
      onChange(event);
    } else {
      // Make sure validate is set to true in the setFieldValue call below otherwise some controls don't update form correctly.
      if (setFieldValue) setFieldValue(name, outputValue, true);
      if (setFieldTouched) setFieldTouched(name, true, false);
    }

    // If user assigned a custom onChange function, fire it off.
    if (props?.onChange && typeof props.onChange === "function") {
      props.onChange(outputValue, event);
    }

    return event;
  };

  // Return an object containing the modified component parameters.
  return {
    name: name,
    value: showValue,
    onChange: fieldOnChange,
    onBlur: onBlur,
    meta: meta,
    form: form,
    field: field,
  };
};

// CHECKBOX - https://material-ui.com/api/checkbox/
export const FormikCheckboxField = (props: ICheckboxFieldProps & IExtendedFieldProps): JSX.Element => {
  const { name, value, format, parse, validate, onChange, ...checkboxProps } = props;
  return (
    <Field name={props.name} validate={props.validate}>
      {(fieldProps: FieldProps) => (
        <CheckboxField
          className="formik_field oc_checkbox"
          {...checkboxProps}
          {...fieldParameters(fieldProps, props)}
        />
      )}
    </Field>
  );
};

// TEXTFIELD - https://material-ui.com/api/text-field/
export const FormikTextField = (props: ITextFieldProps & IExtendedFieldProps): JSX.Element => {
  const { name, value, format, parse, validate, onChange, ...textProps } = props;
  return (
    <Field name={props.name} validate={props.validate} onChange={onChange}>
      {(fieldProps: FieldProps) => (
        <TextField className="formik_field oc_textfield" {...textProps} {...fieldParameters(fieldProps, props)} />
      )}
    </Field>
  );
};

// RICHTEXTFIELD - MUI-RTE (Material-Ui wrapper for DraftJS
// Mui-rte - https://github.com/niuware/mui-rte
// DraftJS - https://draftjs.org/docs/api-reference-editor
export const FormikRichTextField = (props: IRichTextFieldProps & IExtendedFieldProps): JSX.Element => {
  const { name, value, format, parse, validate, onChange, ...richtextProps } = props;
  return (
    <Field name={props.name} validate={props.validate} onChange={onChange}>
      {(fieldProps: FieldProps) => (
        <RichTextField
          className="formik_field oc_richtextfield"
          {...richtextProps}
          {...fieldParameters(fieldProps, props)}
        />
      )}
    </Field>
  );
};

export const FormikPhoneField = (props: IPhoneFieldProps & IExtendedFieldProps): JSX.Element => {
  const { name, value, format, parse, validate, onChange, ...textProps } = props;
  return (
    <Field name={props.name} validate={props.validate} onChange={onChange}>
      {(fieldProps: FieldProps) => (
        <PhoneField className="formik_field oc_phonefield" {...textProps} {...fieldParameters(fieldProps, props)} />
      )}
    </Field>
  );
};

// SELECTFIELD - https://react-select.com/home
export const FormikSelectField = (props: ISelectFieldProps & IExtendedFieldProps): JSX.Element => {
  const { name, value, format, parse, validate, ...selectProps } = props;
  return (
    <Field name={props.name} validate={validate}>
      {(fieldProps: FieldProps) => (
        <SelectField className="formik_field oc_select" {...selectProps} {...fieldParameters(fieldProps, props)} />
      )}
    </Field>
  );
};

// ToggleGroup - https://react-select.com/home
export const FormikToggleGroup = (props: IToggleGroupProps & IExtendedFieldProps): JSX.Element => {
  const { name, value, format, parse, validate, ...toggleProps } = props;
  return (
    <Field name={props.name} validate={validate}>
      {(fieldProps: FieldProps) => (
        <ToggleGroup
          className="formik_field oc_toggle"
          formik={true}
          {...toggleProps}
          {...fieldParameters(fieldProps, props)}
        />
      )}
    </Field>
  );
};

// SELECTFIELD - https://react-select.com/home
export const FormikDraggableSelectField = (props: ISelectFieldProps & IExtendedFieldProps): JSX.Element => {
  const { name, value, format, parse, validate, ...selectProps } = props;
  return (
    <Field name={props.name} validate={validate}>
      {(fieldProps: FieldProps) => {
        //@ts-ignore - Added to resolve a conflict in the type passed in for isOptionDisabled
        return <DraggableSelectField {...selectProps} {...fieldParameters(fieldProps, props)} />;
      }}
    </Field>
  );
};

// DATETIMEFIELD - https://reactdatepicker.com/
export const FormikDateTimeField = (props: IDateTimeFieldProps & IExtendedFieldProps): JSX.Element => {
  const { name, value, format, parse, validate, ...datetimeProps } = props;
  return (
    <Field name={props.name} validate={props.validate}>
      {(fieldProps: FieldProps) => <DateTimeField {...datetimeProps} {...fieldParameters(fieldProps, props)} />}
    </Field>
  );
};

// TIMEFIELD - https://www.npmjs.com/package/maskedinput
export const FormikTimeField = (props: ITimeFieldProps & IExtendedFieldProps): JSX.Element => {
  const { name, value, format, parse, validate, ...timeProps } = props;
  return (
    <Field name={props.name} validate={props.validate}>
      {(fieldProps: FieldProps) => (
        <TimeField className="formik_field oc_time" {...timeProps} {...fieldParameters(fieldProps, props)} />
      )}
    </Field>
  );
};

// SWITCHFIELD - Custom Props
export const FormikSwitchField = (props: ISwitchFieldProps & IExtendedFieldProps): JSX.Element => {
  const { name, value, format, parse, validate, ...switchProps } = props;
  return (
    <Field name={props.name} validate={props.validate}>
      {(fieldProps: FieldProps) => (
        <SwitchField className="formik_field oc_switch" {...switchProps} {...fieldParameters(fieldProps, props)} />
      )}
    </Field>
  );
};

// COLORPICKFIELD - https://casesandberg.github.io/react-color/
export const FormikColorPickField = (props: IColorPickFieldProps & IExtendedFieldProps): JSX.Element => {
  const { name, value, format, parse, validate, onChange, ...colorpickProps } = props;
  return (
    <Field name={props.name} validate={props.validate} onChange={onChange}>
      {(fieldProps: FieldProps) => (
        <ColorPickField
          className="formik_field oc_colorpick"
          {...colorpickProps}
          {...fieldParameters(fieldProps, props)}
        />
      )}
    </Field>
  );
};

// MASKTEXTFIELD
export const FormikMaskTextField = (props: IMaskTextFieldProps & IExtendedFieldProps): JSX.Element => {
  const { name, value, format, parse, validate, onChange, ...textProps } = props;
  return (
    <Field name={props.name} validate={props.validate} onChange={onChange}>
      {(fieldProps: FieldProps) => (
        <MaskTextField
          className="formik_field oc_masktextfield"
          {...textProps}
          {...fieldParameters(fieldProps, props)}
        />
      )}
    </Field>
  );
};
